import React, { useState } from "react";
import { db } from "../firebase-config";
import {
	collection,
	query,
	where,
	getDocs,
	updateDoc,
} from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import "./Auth.css";

function ResetPassword() {
	const [step, setStep] = useState(1);
	const [email, setEmail] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [resetCode, setResetCode] = useState("");
	const [generatedCode, setGeneratedCode] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	// Function to generate a random 6-digit code
	const generateResetCode = () => {
		return Math.floor(100000 + Math.random() * 900000).toString();
	};

	const handleRequestReset = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		try {
			// Check if email exists in database
			const userQuery = query(
				collection(db, "users"),
				where("email", "==", email.toLowerCase())
			);
			const userDocs = await getDocs(userQuery);

			if (userDocs.empty) {
				throw new Error("No account found with this email");
			}

			// Generate reset code
			const code = generateResetCode();
			setGeneratedCode(code);

			// Send reset code via email using your Cloud Function
			const response = await fetch(
				"https://sendresetcode-3guhnqcvsq-uc.a.run.app",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						email: email.toLowerCase(),
						resetCode: code,
					}),
				}
			);

			if (!response.ok) {
				throw new Error("Failed to send reset code");
			}

			setStep(2);
		} catch (error) {
			setError(error.message);
		}
		setLoading(false);
	};

	const handleVerifyCode = (e) => {
		e.preventDefault();
		if (resetCode === generatedCode) {
			setStep(3);
			setError("");
		} else {
			setError("Invalid reset code");
		}
	};

	const handlePasswordReset = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");

		if (newPassword !== confirmPassword) {
			setError("Passwords do not match");
			setLoading(false);
			return;
		}

		try {
			const userQuery = query(
				collection(db, "users"),
				where("email", "==", email.toLowerCase())
			);
			const userDocs = await getDocs(userQuery);
			const userDoc = userDocs.docs[0];

			// Hash the new password using the same method from AuthContext
			const msgBuffer = new TextEncoder().encode(newPassword);
			const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
			const hashArray = Array.from(new Uint8Array(hashBuffer));
			const hashedPassword = hashArray
				.map((b) => b.toString(16).padStart(2, "0"))
				.join("");

			// Update password in database
			await updateDoc(userDoc.ref, {
				password: hashedPassword,
			});

			alert("Password successfully reset!");
			navigate("/login");
		} catch (error) {
			setError("Failed to reset password: " + error.message);
		}
		setLoading(false);
	};

	return (
		<div className="main-container">
			<div className="video-background">
				<video autoPlay loop muted playsInline>
					<source src="/images/49a01d274a.mp4" type="video/mp4" />
				</video>
			</div>

			<div className="auth-container">
				<h1>Reset Password</h1>
				{error && <div className="error">{error}</div>}

				{step === 1 && (
					<form onSubmit={handleRequestReset} className="auth-form">
						<input
							type="email"
							placeholder="Enter your email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
						/>
						<button disabled={loading} type="submit">
							Send Reset Code
						</button>
					</form>
				)}

				{step === 2 && (
					<form onSubmit={handleVerifyCode} className="auth-form">
						<input
							type="text"
							placeholder="Enter reset code"
							value={resetCode}
							onChange={(e) => setResetCode(e.target.value)}
							required
						/>
						<button type="submit">Verify Code</button>
					</form>
				)}

				{step === 3 && (
					<form onSubmit={handlePasswordReset} className="auth-form">
						<input
							type="password"
							placeholder="New password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							required
						/>
						<input
							type="password"
							placeholder="Confirm new password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							required
						/>
						<button disabled={loading} type="submit">
							Reset Password
						</button>
					</form>
				)}

				<div className="auth-switch">
					Remember your password? <Link to="/login">Login</Link>
				</div>
			</div>
		</div>
	);
}

export default ResetPassword;
